import { FormValues, ValidationErrors } from './types'
import { InputFormula } from '../../../api/employer-rules'
import {
  isInputFormulaPath,
  isInputFormulaRule,
  isInputFormulaOperation,
  isInputFormulaEventPath,
  getAvailablePathValuesForContext,
} from './input-formula-utils'

/**
 * Validates the valuesInTime array for overlaps, required fields, and date consistency
 */
export const validateValuesInTime = (values: FormValues['valuesInTime']): ValidationErrors => {
  const errors: ValidationErrors = {}

  // Skip validation if no values
  if (!values?.length) {
    errors['valuesInTime'] = 'Au moins une période est requise'
    return errors
  }

  // Sort valuesInTime by start date to make validation easier
  const sortedValues = [...values].sort(
    (a, b) => new Date(a.effectiveFrom).getTime() - new Date(b.effectiveFrom).getTime(),
  )

  // Check if we have more than one period without end date
  const periodsWithoutEndDate = values.filter(vt => !vt.effectiveTo)
  if (periodsWithoutEndDate.length > 1) {
    periodsWithoutEndDate.forEach(period => {
      const index = values.indexOf(period)
      errors[`valuesInTime.${index}.effectiveTo`] = 'Une seule période peut être sans date de fin'
    })
  }

  // Check if the entry without end date is the most recent one
  const nullEndDateIndex = sortedValues.findIndex(vt => !vt.effectiveTo)
  if (nullEndDateIndex !== -1 && nullEndDateIndex !== sortedValues.length - 1) {
    const periodToCheck = sortedValues[nullEndDateIndex]
    if (periodToCheck) {
      const originalIndex = values.indexOf(periodToCheck)
      if (originalIndex !== -1) {
        errors[`valuesInTime.${originalIndex}.effectiveTo`] =
          'La période sans date de fin doit être la plus récente'
      }
    }
  }

  // Check for overlaps and other validations
  sortedValues.forEach((vt, index) => {
    const periodToCheck = sortedValues[index]
    if (!periodToCheck) return
    const currentIndex = values.indexOf(periodToCheck)
    if (currentIndex === -1) return

    // Basic required field validations with better type checking
    if (isNaN(Number(vt.value)) || (Number(vt.value) > 0 && Number(vt.value) < 1)) {
      errors[`valuesInTime.${currentIndex}.value`] = 'Valeur numérique requise'
    }

    if (!vt.effectiveFrom) {
      errors[`valuesInTime.${currentIndex}.effectiveFrom`] = 'Date de début requise'
    } else {
      // Validate date format
      const fromDate = new Date(vt.effectiveFrom)
      if (fromDate.toString() === 'Invalid Date') {
        errors[`valuesInTime.${currentIndex}.effectiveFrom`] = 'Date invalide'
      }
    }

    if (vt.effectiveTo) {
      // Validate date format
      const toDate = new Date(vt.effectiveTo)
      if (toDate.toString() === 'Invalid Date') {
        errors[`valuesInTime.${currentIndex}.effectiveTo`] = 'Date invalide'
      }

      // Check if end date is after start date
      if (vt.effectiveFrom && vt.effectiveTo < vt.effectiveFrom) {
        errors[`valuesInTime.${currentIndex}.effectiveTo`] =
          'La date de fin doit être après la date de début'
      }
    }

    // Check for overlaps with subsequent periods
    for (let j = index + 1; j < sortedValues.length; j++) {
      const nextVt = sortedValues[j]
      if (!nextVt) continue
      const currentEndDate = vt.effectiveTo ? new Date(vt.effectiveTo) : null
      const nextStartDate = new Date(nextVt.effectiveFrom)

      // If current period has no end date, it automatically overlaps with all subsequent periods
      if (!currentEndDate) {
        errors[`valuesInTime.${currentIndex}.effectiveTo`] =
          'Cette période chevauche les périodes suivantes'
        break
      }

      // If current period's end date is after or equal to next period's start date
      if (currentEndDate >= nextStartDate) {
        errors[`valuesInTime.${currentIndex}.effectiveTo`] =
          'Cette période chevauche la période suivante'
        break
      }
    }
  })

  return errors
}

/**
 * Validates bounds configuration (min/max) based on their modes
 */
export const validateBounds = (interval: FormValues['interval']): ValidationErrors => {
  const errors: ValidationErrors = {}

  // Min bounds validation
  if (interval.minBoundsMode === 'fixed') {
    if (interval.min) {
      const minValue = Number(interval.min)
      if (!isNaN(minValue) && minValue < 0) {
        errors['interval.min'] = 'La valeur minimale doit être positive'
      }
    }
  } else if (interval.minBoundsMode === 'dynamic') {
    if (!interval.minPath) {
      errors['interval.minPath'] = 'Le chemin pour la valeur minimale est obligatoire'
    }
    //if (!isNumber(interval.minFallback)) {
    //  errors['interval.minFallback'] = 'La valeur de fallback pour min est obligatoire'
    //}
    if (interval.minFallback) {
      const minFallbackValue = Number(interval.minFallback)
      if (!isNaN(minFallbackValue) && minFallbackValue < 0) {
        errors['interval.minFallback'] =
          'La valeur de fallback pour min doit être positive ou nulle'
      }
    }
  } else if (interval.minBoundsMode === 'formula') {
    if (interval.mode === 'timeRange') {
      errors['interval.minBoundsMode'] =
        'Les bornes en formule ne sont pas disponibles en mode plage horaire'
    } else if (!interval.minFormula) {
      errors['interval.minFormula'] = 'La formule pour la borne minimale est obligatoire'
    } else {
      Object.assign(
        errors,
        validateInputFormula(interval.minFormula, interval.period, interval.mode, {
          errorField: 'interval.minFormula',
          forbidRuleReferences: true,
        }),
      )
    }
    if (interval.minFallback) {
      const minFallbackValue = Number(interval.minFallback)
      if (!isNaN(minFallbackValue) && minFallbackValue < 0) {
        errors['interval.minFallback'] =
          'La valeur de fallback pour min doit être positive ou nulle'
      }
    }
  }

  // Max bounds validation
  if (interval.maxBoundsMode === 'fixed') {
    if (interval.max) {
      const maxValue = Number(interval.max)
      if (!isNaN(maxValue) && maxValue < 0) {
        errors['interval.max'] = 'La valeur maximale doit être positive'
      }
    }
  } else if (interval.maxBoundsMode === 'dynamic') {
    if (!interval.maxPath) {
      errors['interval.maxPath'] = 'Le chemin pour la valeur maximale est obligatoire'
    }
    if (!interval.maxFallback) {
      errors['interval.maxFallback'] = 'La valeur de fallback pour max est obligatoire'
    }
    if (interval.maxFallback) {
      const maxFallbackValue = Number(interval.maxFallback)
      if (!isNaN(maxFallbackValue) && maxFallbackValue < 0) {
        errors['interval.maxFallback'] =
          'La valeur de fallback pour max doit être positive ou nulle'
      }
    }
  } else if (interval.maxBoundsMode === 'formula') {
    if (interval.mode === 'timeRange') {
      errors['interval.maxBoundsMode'] =
        'Les bornes en formule ne sont pas disponibles en mode plage horaire'
    } else if (!interval.maxFormula) {
      errors['interval.maxFormula'] = 'La formule pour la borne maximale est obligatoire'
    } else {
      Object.assign(
        errors,
        validateInputFormula(interval.maxFormula, interval.period, interval.mode, {
          errorField: 'interval.maxFormula',
          forbidRuleReferences: true,
        }),
      )
    }
    if (interval.maxFallback) {
      const maxFallbackValue = Number(interval.maxFallback)
      if (!isNaN(maxFallbackValue) && maxFallbackValue < 0) {
        errors['interval.maxFallback'] =
          'La valeur de fallback pour max doit être positive ou nulle'
      }
    }
  }

  return errors
}

/**
 * Validates cross-field relationships (e.g., max > min)
 */
export const validateCrossFields = (values: FormValues): ValidationErrors => {
  const errors: ValidationErrors = {}

  /**
   * Cross-validation: only meaningful when both bounds are fixed literals (minutes in the form).
   * Dynamic / formula bounds resolve at runtime; comparing fallbacks or paths here is misleading.
   */
  if (
    values.interval.minBoundsMode === 'fixed' &&
    values.interval.maxBoundsMode === 'fixed' &&
    values.interval.min &&
    values.interval.max
  ) {
    const minValue = Number(values.interval.min)
    const maxValue = Number(values.interval.max)
    if (!isNaN(minValue) && !isNaN(maxValue) && maxValue <= minValue) {
      errors['interval.max'] = 'La valeur maximale doit être supérieure à la valeur minimale'
    }
  }

  return errors
}

/**
 * Validates daysOfWeek selection for day and week period intervals
 */
export const validateDaysOfWeek = (interval: FormValues['interval']): ValidationErrors => {
  const errors: ValidationErrors = {}

  // Validate daysOfWeek when period is 'day' or 'week'
  if (!interval.daysOfWeek || interval.daysOfWeek.length === 0) {
    errors['interval.daysOfWeek'] = 'Au moins un jour de la semaine doit être sélectionné'
  }

  return errors
}

/**
 * Validates specialPeriods configuration
 * Checks that types is an array and has no duplicates
 */
export const validateSpecialPeriods = (
  specialPeriods?: FormValues['specialPeriods'],
): ValidationErrors => {
  const errors: ValidationErrors = {}

  if (!specialPeriods) {
    // specialPeriods is optional, no error if not provided
    return errors
  }

  // Validate types array
  if (specialPeriods.types) {
    if (!Array.isArray(specialPeriods.types)) {
      errors['specialPeriods.types'] = 'Les types doivent être un tableau'
    } else {
      // Check for duplicates
      const uniqueTypes = new Set(specialPeriods.types)
      if (uniqueTypes.size !== specialPeriods.types.length) {
        errors['specialPeriods.types'] = 'Les types ne doivent pas contenir de doublons'
      }
    }
  }

  return errors
}

/**
 * Options for {@link validateInputFormula} when reused for interval bound formulas.
 */
export type ValidateInputFormulaOptions = {
  /** Formik field key for validation messages (default: interval.inputFormula) */
  errorField?: string
  /** When true, reject any `type: 'rule'` node (used for minFormula / maxFormula). */
  forbidRuleReferences?: boolean
  /** When true, skip the root-level "required in duration mode" check (recursive args). */
  skipRootRequirement?: boolean
}

/**
 * Validates InputFormula recursively
 */
export const validateInputFormula = (
  formula: InputFormula | null | undefined,
  period?: string,
  mode?: string,
  opts?: ValidateInputFormulaOptions,
): ValidationErrors => {
  const errors: ValidationErrors = {}
  const field = opts?.errorField ?? 'interval.inputFormula'

  // If timeRange mode, inputFormula is not required (handled automatically)
  if (mode === 'timeRange') {
    return errors
  }

  // For duration mode, inputFormula is required at root (main rule input only)
  if (!opts?.skipRootRequirement && mode === 'duration' && !formula) {
    if (field === 'interval.inputFormula') {
      errors[field] = "La formule d'input est requise en mode durée"
      return errors
    }
    return errors
  }

  if (!formula) {
    return errors
  }

  /**
   * Backend no longer supports constants inside inputFormula.
   * We keep a defensive runtime check here to:
   * - avoid crashing if existing data still contains a legacy constant formula
   * - show a clear validation message so the user can update the rule
   */
  const legacyFormulaType = (formula as unknown as { type?: unknown })?.type
  if (legacyFormulaType === 'constant') {
    errors[field] =
      field === 'interval.inputFormula'
        ? "Les constantes ne sont plus supportées dans la formule d'input. Remplacez-la par un path ou une référence à une règle."
        : 'Les constantes ne sont plus supportées dans cette formule.'
    return errors
  }

  // Get available path values for current context
  const availablePaths = getAvailablePathValuesForContext(period || '', mode)

  // Validate based on formula type
  if (isInputFormulaPath(formula)) {
    // Validate path exists in available paths
    if (!formula.path) {
      errors[field] = 'Le path ne peut pas être vide'
    } else if (availablePaths.length > 0 && !availablePaths.includes(formula.path)) {
      errors[field] = `Le path "${formula.path}" n'est pas valide pour ce contexte`
    }
  } else if (isInputFormulaRule(formula)) {
    if (opts?.forbidRuleReferences) {
      errors[field] = "Les références à d'autres règles ne sont pas autorisées dans cette formule"
      return errors
    }
    // Validate rule ID is present (cannot validate existence without access to rules list)
    if (!formula.ruleId) {
      errors[field] = "L'ID de la règle référencée ne peut pas être vide"
    }
  } else if (isInputFormulaEventPath(formula)) {
    if (!formula.path) {
      errors[field] = "Le path de l'événement ne peut pas être vide"
    } else if (availablePaths.length > 0 && !availablePaths.includes(formula.path)) {
      errors[field] = `Le path "${formula.path}" n'est pas valide pour ce contexte`
    }
    if (!formula.workPeriodEventIds || formula.workPeriodEventIds.length === 0) {
      errors[field] = errors[field] || 'Au moins un événement doit être sélectionné'
    }
  } else if (isInputFormulaOperation(formula)) {
    // Validate operation has at least 2 arguments
    if (!formula.args || formula.args.length < 2) {
      errors[field] = 'Une opération doit avoir au moins 2 arguments'
    } else {
      // Recursively validate each argument
      formula.args.forEach((arg, index) => {
        const argErrors = validateInputFormula(arg, period, mode, {
          ...opts,
          skipRootRequirement: true,
          errorField: field,
        })
        if (Object.keys(argErrors).length > 0) {
          errors[field] = `Erreur dans l'argument ${index + 1} de l'opération`
        }
      })
    }
  } else {
    errors[field] = 'Type de formule invalide'
  }

  return errors
}

/**
 * Main validation function that orchestrates all validations
 */
export const validateEmployerRuleForm = (values: FormValues): ValidationErrors => {
  const errors: ValidationErrors = {}

  // Validate valuesInTime
  const valuesInTimeErrors = validateValuesInTime(values.valuesInTime)
  Object.assign(errors, valuesInTimeErrors)

  // Validate bounds
  const boundsErrors = validateBounds(values.interval)
  Object.assign(errors, boundsErrors)

  // Validate cross-fields
  const crossFieldErrors = validateCrossFields(values)
  Object.assign(errors, crossFieldErrors)

  // Validate daysOfWeek
  const daysOfWeekErrors = validateDaysOfWeek(values.interval)
  Object.assign(errors, daysOfWeekErrors)

  // Validate specialPeriods
  const specialPeriodsErrors = validateSpecialPeriods(values.specialPeriods)
  Object.assign(errors, specialPeriodsErrors)

  // Validate inputFormula
  const inputFormulaErrors = validateInputFormula(
    values.interval.inputFormula,
    values.interval.period,
    values.interval.mode,
  )
  Object.assign(errors, inputFormulaErrors)

  return errors
}
